import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { dataGridCustomizations } from "../../../theme/customizations";
import AppTheme from "../../../theme/AppTheme";
import { CssBaseline } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCampaignById } from "../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { categoryList } from "../../../service/products/ProductServices";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import { formatCouponTypeLabel } from "./utils/couponTypeLabel";
import StoreIcon from "@mui/icons-material/Store";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";

type CampaignData = Record<string, unknown> & {
  id?: string;
  campaign_key?: string;
  title?: string;
  description?: string;
  created_by?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  coupons?: unknown[];
};

type CouponRow = Record<string, unknown> & {
  id?: string | number;
  campaign_id?: string | number;
  code?: string;
  type?: string;
  type_meta?: Record<string, unknown>;
  value?: string | number;
  value_type?: string;
  status?: string;
  max_discount_amount?: string | number;
  min_cart_value?: string | number;
  user_usage_limit?: number;
  global_usage_limit?: string | number;
  start_at?: string;
  end_at?: string;
  created_at?: string;
  updated_at?: string;
};

function addProductKeysToMap(p: Record<string, unknown>, map: Map<string, string>) {
  const name = p?.name != null ? String(p.name).trim() : "";
  if (!name) return;
  const keys: string[] = [];
  if (p.id !== undefined && p.id !== null) keys.push(String(p.id));
  if (p.fid !== undefined && p.fid !== null) keys.push(String(p.fid));
  if (p.reference_code !== undefined && p.reference_code !== null) keys.push(String(p.reference_code));
  if (p.reference_id !== undefined && p.reference_id !== null) keys.push(String(p.reference_id));
  keys.forEach((k) => map.set(k, name));
}

/** Categories → top-level `products` + each `children[].products`; match refs to id, fid, reference_code, reference_id. */
function buildProductRefToNameMap(categories: unknown): Map<string, string> {
  const map = new Map<string, string>();
  const list = Array.isArray(categories) ? categories : [];
  list.forEach((cat: Record<string, unknown>) => {
    const topProducts = cat?.products;
    (Array.isArray(topProducts) ? topProducts : []).forEach((p: unknown) =>
      addProductKeysToMap(p as Record<string, unknown>, map)
    );
    const children = cat?.children;
    (Array.isArray(children) ? children : []).forEach((child: Record<string, unknown>) => {
      const products = child?.products;
      (Array.isArray(products) ? products : []).forEach((p: unknown) =>
        addProductKeysToMap(p as Record<string, unknown>, map)
      );
    });
  });
  return map;
}

/** Match `type_meta.store_reference_id` to an object in `data[]` (`storeId` / `store_id` / `store_reference_id`) and return its store name. */
function findStoreNameInCategoriesData(data: unknown, storeReferenceId: string): string | null {
  const ref = String(storeReferenceId ?? "").trim();
  if (!ref) return null;
  const list = Array.isArray(data) ? data : [];
  const matchesRef = (row: Record<string, unknown>) => {
    const keys = ["storeId", "store_id", "store_reference_id"] as const;
    return keys.some((k) => {
      const v = row[k];
      if (v === undefined || v === null) return false;
      return String(v).trim() === ref;
    });
  };
  const nameFromRow = (row: Record<string, unknown>) => {
    // New API shape provides store name as `storeName` at the top-level category rows.
    const preferred = row.storeName ?? row.store_name;
    if (preferred != null && String(preferred).trim()) return String(preferred).trim();

    // Back-compat fallback (older payloads used `name` on matched store rows).
    const legacy = row.name;
    return legacy != null && String(legacy).trim() ? String(legacy).trim() : null;
  };
  for (const item of list) {
    const row = item as Record<string, unknown>;
    if (matchesRef(row)) {
      const name = nameFromRow(row);
      if (name) return name;
    }
    const children = row.children;
    if (Array.isArray(children)) {
      for (const ch of children) {
        const child = ch as Record<string, unknown>;
        if (matchesRef(child)) {
          const name = nameFromRow(child);
          if (name) return name;
        }
      }
    }
  }
  return null;
}

function collectItemRefs(typeMeta: Record<string, unknown> | undefined): string[] {
  if (!typeMeta || typeof typeMeta !== "object") return [];
  const out: string[] = [];
  const ids = typeMeta.item_reference_ids;
  if (Array.isArray(ids)) {
    ids.forEach((x) => out.push(String(x)));
  }
  const single = typeMeta.item_reference_id;
  if (single !== undefined && single !== null && single !== "") {
    out.push(String(single));
  }
  return Array.from(new Set(out));
}

const ViewCoupon = (props: { disableCustomTheme?: boolean }) => {
  const { campaignId, couponId } = useParams();
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [coupon, setCoupon] = useState<CouponRow | null>(null);
  const [storeProductsLoading, setStoreProductsLoading] = useState(false);
  const [storeName, setStoreName] = useState<string | null>(null);
  /** Matched product names only (same order as item refs); unmatched refs are omitted. */
  const [matchedProductNames, setMatchedProductNames] = useState<string[]>([]);
  const xThemeComponents = { ...dataGridCustomizations };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getOrdinalSuffix = (day: number): string => {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const formatDateOnly = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}${suffix} ${month} ${year}`;
  };

  const displayStr = (v: unknown) => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    const s = String(v).trim();
    return s.length ? s : "-";
  };

  const fetchCoupon = useCallback(async () => {
    if (!campaignId || !couponId) return;
    try {
      setLoading(true);
      const response = await getCampaignById(campaignId);
      const campaignData =
        response?.data?.campaign ||
        response?.data?.data ||
        response?.data ||
        response?.campaign ||
        null;
      setCampaign(campaignData || null);
      const list = campaignData?.coupons;
      const rows: CouponRow[] = Array.isArray(list) ? list : [];
      const found = rows.find((c) => String(c?.id) === String(couponId)) || null;
      setCoupon(found);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifyError(err?.response?.data?.message || "Failed to load coupon details");
      setCoupon(null);
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId, couponId]);

  useEffect(() => {
    fetchCoupon();
  }, [fetchCoupon]);

  const storeRef = useMemo(() => {
    const tm = coupon?.type_meta;
    if (!tm || typeof tm !== "object") return "";
    const id = (tm as { store_reference_id?: unknown }).store_reference_id;
    if (id === undefined || id === null) return "";
    const s = String(id).trim();
    return s;
  }, [coupon?.type_meta]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setStoreName(null);
      setMatchedProductNames([]);
      if (!storeRef) {
        setStoreProductsLoading(false);
        return;
      }
      setStoreProductsLoading(true);
      try {
        const catRes = await categoryList(storeRef);
        if (cancelled) return;
        const categories = catRes?.data?.data ?? [];
        setStoreName(findStoreNameInCategoriesData(categories, storeRef));
        const productMap = buildProductRefToNameMap(categories);
        const refs = collectItemRefs(coupon?.type_meta);
        const names: string[] = [];
        refs.forEach((ref) => {
          const n = productMap.get(ref);
          if (n) names.push(n);
        });
        setMatchedProductNames(names);
      } catch {
        if (!cancelled) {
          notifyError("Failed to load product categories");
          setMatchedProductNames([]);
        }
      } finally {
        if (!cancelled) setStoreProductsLoading(false);
      }
    };
    if (coupon) run();
    return () => {
      cancelled = true;
    };
  }, [coupon, storeRef]);

  /** 22 fields (no Status — status is only in section header). Split 8 + 7 + 7 across three columns. */
  const couponBodyRows = useMemo(() => {
    if (!coupon) return [];
    return [
      { label: "Code", value: displayStr(coupon.code) },
      { label: "Type", value: formatCouponTypeLabel(coupon.type as string | undefined) },
      { label: "Value", value: displayStr(coupon.value) },
      { label: "Value Type", value: displayStr(coupon.value_type) },
      { label: "Max Discount Amount", value: displayStr(coupon.max_discount_amount) },
      { label: "Min Cart Value", value: displayStr(coupon.min_cart_value) },
      { label: "Valid Pincodes", value: displayStr(coupon.valid_pincodes) },
      { label: "Valid Radius Center", value: displayStr(coupon.valid_radius_center) },
      { label: "Valid Radius (km)", value: displayStr(coupon.valid_radius_km) },
      { label: "Applicable Store IDs", value: displayStr(coupon.applicable_store_ids) },
      { label: "User Usage Limit", value: displayStr(coupon.user_usage_limit) },
      {
        label: "Global Usage Limit",
        value: (() => {
          const v = coupon.global_usage_limit;
          if (v === null || v === undefined) return "-";
          const s = String(v).trim();
          return s ? s : "-";
        })(),
      },
      { label: "Per Store Limit", value: displayStr(coupon.per_store_limit) },
      { label: "Stackable", value: displayStr(coupon.stackable) },
      { label: "Priority", value: displayStr(coupon.priority) },
      { label: "Exported", value: displayStr(coupon.exported) },
      { label: "Exported By", value: displayStr(coupon.exported_by) },
      { label: "Exported At", value: formatDateTime(coupon.exported_at as string | undefined) },
      { label: "Start At", value: formatDateOnly(coupon.start_at) },
      { label: "End At", value: formatDateOnly(coupon.end_at) },
      { label: "Created At", value: formatDateTime(coupon.created_at) },
      { label: "Updated At", value: formatDateTime(coupon.updated_at) },
    ];
  }, [coupon]);

  const couponCols = useMemo(() => {
    const r = couponBodyRows;
    return [r.slice(0, 8), r.slice(8, 15), r.slice(15, 22)] as const;
  }, [couponBodyRows]);

  const couponStatusChip = useMemo(() => {
    if (!coupon) return null;
    const raw = (coupon.status ?? "").toString();
    const label = raw
      ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
      : "-";
    const normalized = raw.trim().toLowerCase();
    return (
      <Chip
        label={label}
        size="small"
        color={normalized === "active" ? "success" : "default"}
        variant="outlined"
      />
    );
  }, [coupon]);

  const campaignStatusChip = useMemo(() => {
    if (!campaign) return null;
    const raw = (campaign.status ?? "").toString();
    const label = raw
      ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
      : "-";
    const normalized = raw.trim().toLowerCase();
    return (
      <Chip
        label={label}
        size="small"
        color={normalized === "active" ? "success" : "default"}
        variant="outlined"
      />
    );
  }, [campaign]);

  const couponTypeMeta = coupon?.type_meta as Record<string, unknown> | undefined;

  const isFreeDeliveryYes = useMemo(() => {
    if (!couponTypeMeta) return false;
    const v = couponTypeMeta.free_delivery;
    if (v === true) return true;
    if (typeof v === "string" && v.trim().toLowerCase() === "yes") return true;
    return false;
  }, [couponTypeMeta]);

  const isFreeDeliveryNo = useMemo(() => {
    if (!couponTypeMeta) return false;
    const v = couponTypeMeta.free_delivery;
    if (v === false) return true;
    if (typeof v === "string" && v.trim().toLowerCase() === "no") return true;
    return false;
  }, [couponTypeMeta]);

  const deliveryFeeCapDisplay = useMemo(() => {
    if (!couponTypeMeta) return "—";
    const v = couponTypeMeta.delivery_fee_cap;
    if (v === null || v === undefined) return "—";
    const s = String(v).trim();
    return s.length ? s : "—";
  }, [couponTypeMeta]);

  const hasStoreNameResolved = useMemo(() => {
    const n = storeName;
    return typeof n === "string" && n.trim().length > 0;
  }, [storeName]);

  /** Hide the card when there is no store, no products, no free-delivery Yes, and not loading
   *  (including when free_delivery is only No with nothing else to show). */
  const showStoreProductsSection = useMemo(() => {
    if (storeProductsLoading) return true;
    if (hasStoreNameResolved) return true;
    if (matchedProductNames.length > 0) return true;
    if (isFreeDeliveryYes) return true;
    return false;
  }, [
    storeProductsLoading,
    hasStoreNameResolved,
    matchedProductNames.length,
    isFreeDeliveryYes,
  ]);

  /** One grid per column group so label column width = widest label and values share a vertical alignment. */
  const renderCouponPairRows = (rows: { label: string; value: ReactNode }[]) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, 1fr)",
        columnGap: 2.5,
        rowGap: 0.75,
        alignItems: "start",
        minWidth: 0,
        width: "100%",
      }}
    >
      {rows.flatMap((row, idx) => [
        <Typography
          key={`coupon-k-${idx}`}
          variant="body2"
          component="div"
          sx={{
            fontWeight: 700,
            minWidth: 0,
            maxWidth: "100%",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {row.label}
        </Typography>,
        <Typography
          key={`coupon-v-${idx}`}
          variant="body2"
          component="div"
          sx={{
            textAlign: "left",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {row.value}
        </Typography>,
      ])}
    </Box>
  );

  const paperSx = {
    p: 2,
    borderRadius: 2,
    border: (t: { palette: { divider: string } }) => `1px solid ${t.palette.divider}`,
    boxShadow: "none" as const,
    mb: 2,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box" as const,
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, minWidth: 0, boxSizing: "border-box" }}>
        <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
          View Coupon
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : coupon ? (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {campaign ? (
              <Paper elevation={0} sx={paperSx}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1.25,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Campaign
                  </Typography>
                  {campaignStatusChip}
                </Box>
                {isMobile ? (
                  <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Campaign Key
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "58%", wordBreak: "break-word" }}>
                        {displayStr(campaign.campaign_key)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Title
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "58%", wordBreak: "break-word" }}>
                        {displayStr(campaign.title)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Description
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "58%", wordBreak: "break-word" }}>
                        {displayStr(campaign.description)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Created By
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "58%", wordBreak: "break-word" }}>
                        {displayStr(campaign.created_by)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Created At
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "right", flexShrink: 0, whiteSpace: "nowrap", maxWidth: "100%" }}
                      >
                        {formatDateTime(campaign.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Updated At
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "right", flexShrink: 0, whiteSpace: "nowrap", maxWidth: "100%" }}
                      >
                        {formatDateTime(campaign.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "120px 1fr", sm: "140px 1fr" },
                          columnGap: 1.5,
                          rowGap: 1,
                          alignItems: "start",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Campaign Key
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {displayStr(campaign.campaign_key)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Title
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {displayStr(campaign.title)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {displayStr(campaign.description)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Created By
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {displayStr(campaign.created_by)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        flexShrink: 0,
                        minWidth: 0,
                        width: "auto",
                        maxWidth: "100%",
                        display: "grid",
                        gridTemplateColumns: "minmax(88px, max-content) minmax(0, max-content)",
                        columnGap: 1.5,
                        rowGap: 1,
                        alignItems: "start",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Created At
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(campaign.created_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Updated At
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(campaign.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            ) : null}

            {showStoreProductsSection ? (
              <Paper elevation={0} sx={paperSx}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                  Store &amp; Products
                </Typography>
                {storeProductsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      Loading store and products…
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {(hasStoreNameResolved || isFreeDeliveryYes || isFreeDeliveryNo) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        {hasStoreNameResolved ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            <StoreIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                              {storeName}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ flex: 1, minWidth: 0 }} />
                        )}
                        {(isFreeDeliveryYes || isFreeDeliveryNo) && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              flexShrink: 0,
                              ml: { xs: 0, sm: "auto" },
                            }}
                          >
                            <TwoWheelerIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {isFreeDeliveryYes ? "Free Delivery Available" : "No Free Delivery"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    {(matchedProductNames.length > 0 || isFreeDeliveryYes) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {matchedProductNames.length > 0
                            ? matchedProductNames.map((name, idx) => (
                                <Box
                                  key={`${idx}-${name}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: idx < matchedProductNames.length - 1 ? 0.75 : 0,
                                  }}
                                >
                                  <Inventory2Icon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                    {name}
                                  </Typography>
                                </Box>
                              ))
                            : null}
                        </Box>
                        {isFreeDeliveryYes ? (
                          <Box
                            sx={{
                              flexShrink: 0,
                              alignSelf: "flex-start",
                              display: "flex",
                              alignItems: "baseline",
                              justifyContent: "flex-end",
                              gap: 0.75,
                              flexWrap: "wrap",
                              textAlign: "right",
                              ml: matchedProductNames.length === 0 ? "auto" : 0,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" component="span">
                              Delivery Fee Cap
                            </Typography>
                            <Typography variant="body2" component="span" fontWeight={500}>
                              {deliveryFeeCapDisplay}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ) : null}

            <Paper elevation={0} sx={paperSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Coupon
                </Typography>
                {couponStatusChip}
              </Box>
              {isBelowMd ? (
                renderCouponPairRows([
                  ...couponCols[0],
                  ...couponCols[1],
                  ...couponCols[2],
                ])
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    columnGap: { md: 3, lg: 4 },
                    rowGap: 0,
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ minWidth: 0, overflow: "hidden", pr: { md: 1 } }}>
                    {renderCouponPairRows(couponCols[0])}
                  </Box>
                  <Box sx={{ minWidth: 0, overflow: "hidden", px: { md: 1 } }}>
                    {renderCouponPairRows(couponCols[1])}
                  </Box>
                  <Box sx={{ minWidth: 0, overflow: "hidden", pl: { md: 1 } }}>
                    {renderCouponPairRows(couponCols[2])}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Coupon not found.
          </Typography>
        )}
      </Box>
    </AppTheme>
  );
};

export default ViewCoupon;
